import classNames from 'classnames';
import Slider from 'rc-slider';
import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3 } from '../../components/Button/ButtonV3';
import type { GraphModalInstance } from '../../components/GraphModal/GraphModal';
import { failureToast } from '../../components/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import type { Author, Guest, User } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import type { VoteData } from '../api/thread';
import { deleteVote, postVote } from '../api/thread';
import { AuthorAvatar } from '../AuthorAvatar';
import { allVotesMap, getVoteIcon } from './icons';
import type { VoteResp } from './vote-model';

const messages = defineMessages({
  deny: { defaultMessage: 'Non merci', id: 'KVaAkH' },
  booster: { defaultMessage: 'Vote Booster', id: 'd41zXE' },
  voteFailed: { defaultMessage: "Votre vote n'a pas pu être pris en compte", id: 'JlBhFB' },
  titleYouAnsweredTo: { defaultMessage: 'Vous avez répondu à', id: 'wdHHAo' },
  paragraphEvaluateComment: { defaultMessage: 'Evaluer son commentaire\u00a0?', id: 'r/RlUn' },
});

export const intlVotes = defineMessages({
  ratings0: { defaultMessage: 'Brillant', id: '+8SnWn' },
  ratings1: { defaultMessage: 'Pertinent', id: 'PLvOvE' },
  ratings2: { defaultMessage: "J'aime", id: 'JPtB22' },
  ratings3: { defaultMessage: 'Sans opinion', id: '3TkvDQ' },
  ratings4: { defaultMessage: "J'aime pas", id: 'xebsku' },
  ratings5: { defaultMessage: 'Troll', id: 'juWW/7' },
  ratings6: { defaultMessage: 'Hors charte', id: '+logMO' },
});

const allVotes = Object.entries(allVotesMap).sort(
  ([value1], [value2]) => Number(value2) - Number(value1),
);

/**
 *
 * @param repliedAuthor
 * @param setScore
 * @constructor
 */
export const ContextRepliedAuthor = ({
  repliedAuthor,
  cancel,
}: {
  repliedAuthor?: Author;
  cancel: VoidFunction;
}) => (
  <div
    className={classNames(
      'flex-1 flex flex-col items-center justify-center max-w-full space-y-2 md:space-y-4',
      'md:h-full md:text-center md:rounded-md border',
      'border-gray-250 p-2 md:p-4 md:min-w-[220px]',
    )}
  >
    <div className="uppercase text-gray-medium font-semibold">
      <FormattedMessage {...messages.titleYouAnsweredTo} />
    </div>
    <div className="flex md:flex-col items-center justify-center w-full _md:space-x-4 md:space-y-4">
      <AuthorAvatar
        author={repliedAuthor}
        userId={(repliedAuthor as User).id}
        profile={(repliedAuthor as User).profile}
        size="vbig"
      />
      <h2 className="text-primary-base text-base truncate">
        {(repliedAuthor as Guest)?.username ?? (repliedAuthor as User)?.profile?.pseudo}
      </h2>
    </div>
    <div className="flex flex-col items-center space-y-2">
      <strong className="text-base w-max">
        <FormattedMessage {...messages.paragraphEvaluateComment} />
      </strong>
      <div className="inline whitespace-nowrap md:block">
        <ButtonV3 onClick={cancel} variant="text">
          <FormattedMessage {...messages.deny} />
        </ButtonV3>
      </div>
    </div>
  </div>
);

interface Props {
  controller: GraphModalInstance;
  url: string;
  onVote?: (data: VoteResp) => void;
  currentVote: VoteData;
  repliedAuthor: Author | null;
  author0?: Author;
}

export const VoteContent: FC<Props> = (props: Props) => {
  const { controller, url, onVote, currentVote, repliedAuthor, author0 } = props;
  const { authUserId, isAuthDone, isModerator, login } = usePermissions();
  const [boost, setBoost] = useState(currentVote?.boost ?? 0);
  const hasModeratorRights = isModerator;
  const initialScore = useRef(currentVote?.score || 0).current;

  const closeVote = useCallback(() => controller?.hide(), [controller]);

  const vote = useCallback(
    async (payload: VoteData | undefined) => {
      try {
        const data = await (payload ? postVote(url, payload) : deleteVote(url));

        if (onVote) {
          onVote(data);
        }

        closeVote();
      } catch (error) {
        handleError(error);
        failureToast(<FormattedMessage {...messages.voteFailed} />);
      }
    },
    [url, onVote, closeVote],
  );

  const handleOnChangeBoost = (value: number | number[]) => {
    setBoost(value as number);
  };

  const handleOnChange = useCallback(
    (value: number | number[]) => {
      if (value === null) {
        return;
      }

      if (!authUserId && isAuthDone && typeof login === 'function') {
        login();
        return;
      }

      // if (value) {
      vote({
        boost,
        score: value as number,
      });
      // } else {
      //   vote(undefined);
      // }
    },
    [authUserId, boost, isAuthDone, login, vote],
  );

  const marks = useMemo(
    () =>
      Object.fromEntries(
        allVotes.map(([value, name], index) => [
          value,
          <div key={name} className="flex flex-row items-center text-base relative">
            <div className="text-sm mr-9">{getVoteIcon(name, 'large')}</div>
            {/* Width aligned with the 214px below (with extra margin) */}
            <div
              className="text-gray font-semibold w-[120px] text-left"
              onClick={() => handleOnChange(3 - index)}
            >
              <FormattedMessage {...intlVotes[`ratings${index ?? 0}` as keyof typeof intlVotes]} />
            </div>
          </div>,
        ]),
      ),
    [handleOnChange],
  );

  const voteSection = (
    <div className="md:flex-1">
      <div
        className={classNames(
          'votesContainer vote py-4 px-10 flex flex-col space-y-8 items-center justify-center',
          hasModeratorRights ? 'vote-has-boost' : 'pt-8',
          {
            'vote-required': false,
          },
        )}
      >
        {hasModeratorRights && (
          <div className="vote-boost text-center p-4 self-stretch">
            <p className="font-bold text-md text-primary mb-3">
              <FormattedMessage {...messages.booster} />
            </p>

            <Slider
              min={0}
              max={100}
              step={10}
              marks={{ 0: 0, 50: 50, 100: 100 }}
              defaultValue={boost as number}
              onChange={handleOnChangeBoost}
              dotStyle={{ borderWidth: 0, width: 0 }}
              railStyle={{ top: '2px', left: '-10%', width: '120%', border: '5px solid #f3f3f3' }}
              trackStyle={{
                left: '-5px',
                background: 'linear-gradient(to left, #ef5c5a 0%, #ffea0c 100%)',
              }}
            />
          </div>
        )}

        {/* Width aligned with the 120px above (with extra margin) */}
        <div className="vote-slider w-[184px]">
          <Slider
            className="h-56"
            vertical
            min={-3}
            max={3}
            marks={marks}
            step={null}
            defaultValue={initialScore}
            onChangeComplete={handleOnChange}
            dotStyle={{ borderWidth: 0, width: 0 }}
            railStyle={{
              left: '2px',
              top: '-5%',
              height: '110%',
              border: '5px solid #f3f3f3',
            }}
            trackStyle={{
              background: 'linear-gradient(to bottom, #ef5c5a 0%, #ffea0c 100%)',
              bottom: '-6px',
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row _md:space-y-4">
      {(repliedAuthor || author0) && (
        <div className="md:flex-1 flex">
          <div className="flex-1 flex items-center px-4 py-4 md:px-4 md:py-6 rounded bg-gray-50">
            <ContextRepliedAuthor repliedAuthor={repliedAuthor || author0} cancel={closeVote} />
          </div>
        </div>
      )}
      {voteSection}
    </div>
  );
};
